import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator,
    faCartShopping,
    faCopy,
    faDownload,
    faFloppyDisk,
    faListCheck,
    faRotateLeft,
    faRightToBracket,
    faPaperPlane,
    faArrowLeft,
    faEllipsisVertical,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import {
    CalculateResponse,
    LeistungenResponse,
    OfferFragments,
    RequirementGroup,
    RequirementItem,
    Rubric,
    useCalculatorApi,
} from './calculatorApi';
import {
    collectProject,
    configuredRequirementIds,
    Instance,
    InstanceMap,
    newInstance,
    projectToInstances,
    defaultOptions,
    defaultStandort,
    withDefaultStandort,
} from './instanceModel';
import { Standort } from 'apiClient/Api';
import { StandortManager } from './StandortManager';
import { RequirementInputTree } from './RequirementInputTree';
import { Warenkorb } from './Warenkorb';
import { SaveProjectDialog, SaveProjectValues } from './ProjectStore';
import { CreatedShareLink, SendInvitationDialog, SendInvitationValues } from './SendInvitationDialog';
import { ProjectOverview } from './ProjectOverview';
import { CustomerNoteBanner } from './CustomerNoteBanner';
import { CustomerDetailsView, hasCustomerDetails } from './CustomerDetails';
import { CustomerDetails, SaveProjectRequest, StoredProject } from 'apiClient/Api';

const money = (v: number) =>
    Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const indexTree = (group: RequirementGroup, into: Map<string, RequirementItem>) => {
    for (const i of group.items) into.set(i.id, i);
    for (const s of group.groups) indexTree(s, into);
};

const emptyInstances = (index: Map<string, RequirementItem>): InstanceMap => {
    const map: InstanceMap = {};
    index.forEach((item, id) => {
        map[id] = [newInstance(0, defaultOptions(item.optionGroups))];
    });
    return map;
};

// Ergebnis-Tabs.
const CART_TAB = 0;
const FRAGMENT_TAB = 1;
const LV_TAB = 2;
const LOG_TAB = 3;

const stripTags = (html: string) => html.replace(/<[^>]+>/g, '');

// Fallback für Kontexte ohne Clipboard-API (z. B. App über HTTP/IP statt HTTPS/localhost).
const execCommandCopyText = (text: string): boolean => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }
    document.body.removeChild(ta);
    return ok;
};

const copyPlainText = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fällt auf execCommand zurück */
    }
    return execCommandCopyText(text);
};

const HtmlFragment: React.FC<{ html: string }> = ({ html }) => (
    <Box
        sx={{
            '& table': { borderCollapse: 'collapse', width: '100%', mt: 2 },
            '& th, & td': { border: (t) => `1px solid ${t.palette.divider}`, p: 0.75, textAlign: 'left' },
            '& th': { bgcolor: 'action.hover' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

export const Calculation: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle, apiError, api, canSeePrices, hasPermission } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId?: string }>();
    // OFFICE+ / project.link.manage starten auf der Übersicht; Endkunden direkt im Konfigurator.
    const startsOnOverview = hasPermission('project.link.manage');
    const [mode, setMode] = useState<'overview' | 'configure'>(
        projectId ? 'configure' : startsOnOverview ? 'overview' : 'configure',
    );
    // Zuletzt aus der URL geladenes Projekt (verhindert Doppel-Laden).
    const loadedIdRef = useRef<string | null>(null);
    const calc = useCalculatorApi();
    // Verstecktes Element für den Rich-Text-Fallback (execCommand auf einer DOM-Auswahl).
    const clipRef = useRef<HTMLDivElement>(null);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [tree, setTree] = useState<RequirementGroup | null>(null);
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [index, setIndex] = useState<Map<string, RequirementItem>>(new Map());
    const [instances, setInstances] = useState<InstanceMap>({});
    const [standorte, setStandorte] = useState<Standort[]>([defaultStandort()]);
    const [activeStandort, setActiveStandort] = useState<string>('A');
    const [projektname, setProjektname] = useState('');
    const [reqOrder, setReqOrder] = useState<'xml' | 'name'>(
        (localStorage.getItem('ok-req-order') as 'xml' | 'name') || 'xml',
    );

    const [response, setResponse] = useState<CalculateResponse | null>(null);
    const [resultTab, setResultTab] = useState(0);
    // Es ist immer nur eine Ansicht sichtbar: Anforderungen ODER Kalkulation/Warenkorb.
    const [view, setView] = useState<'input' | 'result'>('input');

    // Leistungsmerkmale (lazy: erst beim Öffnen des Tabs geladen)
    const [leistungen, setLeistungen] = useState<LeistungenResponse | null>(null);
    const [leistungenStale, setLeistungenStale] = useState(true);
    const [leistungenLoading, setLeistungenLoading] = useState(false);
    const [lvSelected, setLvSelected] = useState<Set<string>>(new Set());
    const [lvMuss, setLvMuss] = useState(false);
    const lastProjectRef = useRef<ReturnType<typeof collectProject> | null>(null);

    const [copyTable, setCopyTable] = useState(false);
    const [labelwinEnabled, setLabelwinEnabled] = useState(false);

    // Projektspeicher (DB)
    const [storedId, setStoredId] = useState<string | null>(null);
    const [storedName, setStoredName] = useState('');
    const [customerNotes, setCustomerNotes] = useState<string | null>(null);
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
    const [customerName, setCustomerName] = useState('');
    const [customerReference, setCustomerReference] = useState('');
    const [supportContractId, setSupportContractId] = useState<string | null>(null);
    const [saveOpen, setSaveOpen] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const calculatedAtRef = useRef<string | null>(null);

    useEffect(() => setTitle(t('calculation.title')), [setTitle, t]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [rubricList, requirementTree] = await Promise.all([
                    calc.listRubrics().catch(() => [] as Rubric[]),
                    calc.getRequirements(),
                ]);
                if (!active) return;
                const idx = new Map<string, RequirementItem>();
                indexTree(requirementTree, idx);
                setRubrics(rubricList);
                setTree(requirementTree);
                setIndex(idx);
                setInstances(emptyInstances(idx));
                calc.labelwinStatus()
                    .then((s) => active && setLabelwinEnabled(!!s.enabled))
                    .catch(() => undefined);
            } catch (e) {
                apiError(e);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const configured = useMemo(() => configuredRequirementIds(instances), [instances]);

    // Falls der gewählte Standort entfernt wurde → zurück auf den Standard.
    useEffect(() => {
        if (!standorte.some((s) => s.id === activeStandort)) setActiveStandort('A');
    }, [standorte, activeStandort]);

    // ---- Instanz-Mutatoren (standort-bezogen) ----
    const addInstance = useCallback(
        (reqId: string, area: string) => {
            const defaults = defaultOptions(index.get(reqId)?.optionGroups);
            const inst = newInstance(0, defaults);
            inst.area = area;
            setInstances((prev) => ({ ...prev, [reqId]: [...(prev[reqId] || []), inst] }));
        },
        [index],
    );

    // Stellt sicher, dass jede angegebene Anforderung für den Standort `area` mindestens eine
    // (leere) Instanz hat – Vorbedingung, damit der Konfigurator je Standort Eingabefelder zeigt.
    const ensureInstances = useCallback(
        (reqIds: string[], area: string) => {
            setInstances((prev) => {
                let changed = false;
                const next = { ...prev };
                for (const reqId of reqIds) {
                    const list = next[reqId] || [];
                    if (!list.some((i) => (i.area || 'A') === area)) {
                        const inst = newInstance(0, defaultOptions(index.get(reqId)?.optionGroups));
                        inst.area = area;
                        next[reqId] = [...list, inst];
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        },
        [index],
    );

    const removeInstance = useCallback((reqId: string, iid: number) => {
        setInstances((prev) => {
            const target = (prev[reqId] || []).find((i) => i.iid === iid);
            const area = target?.area || 'A';
            const next = (prev[reqId] || []).filter((i) => i.iid !== iid);
            // Pro Standort soll immer eine (leere) Zeile bleiben.
            if (!next.some((i) => (i.area || 'A') === area)) {
                const empty = newInstance(0, defaultOptions(index.get(reqId)?.optionGroups));
                empty.area = area;
                next.push(empty);
            }
            return { ...prev, [reqId]: next };
        });
    }, [index]);

    const updateInstance = useCallback((reqId: string, iid: number, patch: Partial<Instance>) => {
        setInstances((prev) => ({
            ...prev,
            [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, ...patch } : i)),
        }));
    }, []);

    const updateOpt = useCallback(
        (reqId: string, iid: number, fn: (prev: Record<string, number>) => Record<string, number>) => {
            setInstances((prev) => ({
                ...prev,
                [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, opt: fn(i.opt) } : i)),
            }));
        },
        [],
    );

    const changeReqOrder = (value: 'xml' | 'name') => {
        setReqOrder(value);
        localStorage.setItem('ok-req-order', value);
    };

    // ---- Aktionen ----
    const calculate = useCallback(async () => {
        const project = collectProject(projektname, instances, index, standorte);
        if ((project.instanzen?.length ?? 0) === 0) {
            enqueueSnackbar(t('calculation.noInput'), { variant: 'warning' });
            return;
        }
        setBusy(true);
        try {
            const res = await calc.calculateProject({ project });
            setResponse(res);
            lastProjectRef.current = project;
            calculatedAtRef.current = new Date().toISOString();
            setLeistungen(null);
            setLeistungenStale(true);
            setResultTab(0);
            setView('result');
        } catch (e) {
            apiError(e);
        } finally {
            setBusy(false);
        }
    }, [projektname, instances, index, calc, enqueueSnackbar, t, apiError]);

    const fetchLeistungen = useCallback(
        async (resetDomains: boolean) => {
            const project = lastProjectRef.current;
            if (!project) return;
            const domains = !resetDomains && lvSelected.size ? Array.from(lvSelected) : null;
            setLeistungenLoading(true);
            try {
                const res = await calc.generateLeistungen({ project, domains });
                setLeistungen(res);
                setLeistungenStale(false);
                if (resetDomains) setLvSelected(new Set(res.availableDomains || []));
            } catch (e) {
                apiError(e);
            } finally {
                setLeistungenLoading(false);
            }
        },
        [calc, lvSelected, apiError],
    );

    // Leistungsmerkmale beim Öffnen des Tabs (Index 2) laden, sofern noch nicht geschehen.
    useEffect(() => {
        if (resultTab === LV_TAB && response && leistungenStale && !leistungenLoading) {
            fetchLeistungen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultTab, response, leistungenStale]);

    const onResultTabChange = (_: React.SyntheticEvent, value: number) => setResultTab(value);

    const toggleDomain = (domain: string, checked: boolean) => {
        const next = new Set(lvSelected);
        if (checked) next.add(domain);
        else next.delete(domain);
        setLvSelected(next);
        const project = lastProjectRef.current;
        if (project) {
            calc.generateLeistungen({ project, domains: next.size ? Array.from(next) : null })
                .then((res) => {
                    setLeistungen(res);
                    setLeistungenStale(false);
                })
                .catch(apiError);
        }
    };

    const reset = () => {
        setInstances(emptyInstances(index));
        setStandorte([defaultStandort()]);
        setResponse(null);
        setLeistungen(null);
        setLeistungenStale(true);
        lastProjectRef.current = null;
        setView('input');
        setStoredId(null);
        setStoredName('');
        setCustomerNotes(null);
        setCustomerDetails(null);
        setCustomerName('');
        setCustomerReference('');
        setSupportContractId(null);
    };

    // ---- Projektspeicher (DB) ----
    const buildSaveBody = (name: string, values?: SaveProjectValues): SaveProjectRequest => ({
        id: storedId,
        name,
        project: collectProject(projektname, instances, index, standorte),
        customerReference: (values?.customerReference ?? customerReference) || null,
        customerName: (values?.customerName ?? customerName) || null,
        supportContractId: (values?.supportContractId ?? supportContractId) || null,
        lastCalculation: response
            ? {
                  totalNet: response.result.summary.gesamt,
                  positionCount: response.result.positions.filter((p) => p.kind === 'Visible' || p.kind === 'Hidden')
                      .length,
                  calculatedAt: calculatedAtRef.current ?? new Date().toISOString(),
              }
            : null,
    });

    const applyStored = (s: StoredProject) => {
        setStoredId(s.id ?? null);
        setStoredName(s.name ?? '');
        setCustomerName(s.customerName ?? '');
        setCustomerReference(s.customerReference ?? '');
        setSupportContractId(s.supportContractId ?? null);
    };

    // „Speichern": überschreibt das geladene Projekt (PUT) oder öffnet den Dialog für eine Neuanlage.
    const handleSave = async () => {
        if (!storedId) {
            setSaveOpen(true);
            return;
        }
        try {
            const r = await api.calculator.projectsUpdate(storedId, buildSaveBody(storedName));
            applyStored(r.data as unknown as StoredProject);
            enqueueSnackbar(t('calculation.store.saved'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    // „Speichern unter": legt immer neu an (POST).
    const handleSaveSubmit = async (values: SaveProjectValues) => {
        try {
            const body = { ...buildSaveBody(values.name, values), id: null };
            const r = await api.calculator.projectsCreate(body);
            applyStored(r.data as unknown as StoredProject);
            setProjektname((p) => p || values.name);
            enqueueSnackbar(t('calculation.store.saved'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    // „Neue Einladung versenden" in einem Schritt: aktuelles Projekt speichern (Anlage/Update)
    // und danach einen Freigabe-Link erstellen + per E-Mail an den Kunden verschicken.
    const handleSendInvitation = async (values: SendInvitationValues): Promise<CreatedShareLink | null> => {
        try {
            const name = values.name.trim() || storedName || projektname || t('calculation.invite.defaultName', 'Projekt');
            const body = buildSaveBody(name, {
                name,
                customerName: values.customerName,
                customerReference: values.customerReference,
                supportContractId,
            });
            const saved = storedId
                ? ((await api.calculator.projectsUpdate(storedId, body)).data as unknown as StoredProject)
                : ((await api.calculator.projectsCreate({ ...body, id: null })).data as unknown as StoredProject);
            applyStored(saved);
            setProjektname((p) => p || name);
            if (!saved.id) throw new Error('missing project id');
            const created = (
                await api.calculator.shareLinkCreate(saved.id, {
                    mode: values.mode,
                    customerEmail: values.customerEmail,
                    expiresAt: values.expiresAt,
                })
            ).data as unknown as CreatedShareLink;
            enqueueSnackbar(
                created.emailSent
                    ? t('calculation.invite.sentToast', 'Einladung gesendet.')
                    : t('calculation.invite.sentNoMailToast', 'Link erstellt – E-Mail-Versand fehlgeschlagen.'),
                { variant: created.emailSent ? 'success' : 'warning' },
            );
            return created;
        } catch (e) {
            apiError(e);
            return null;
        }
    };

    const handleLoadProject = async (id: string) => {
        try {
            const r = await api.calculator.projectsGet(id);
            const s = r.data as unknown as StoredProject;
            const project = s.project ?? { projektname: '', instanzen: [] };
            setProjektname(project.projektname || s.name || '');
            setInstances({ ...emptyInstances(index), ...projectToInstances(project) });
            setStandorte(withDefaultStandort(project.standorte));
            applyStored(s);
            setCustomerNotes((s as unknown as { customerNotes?: string | null }).customerNotes ?? null);
            setCustomerDetails((s as unknown as { customerDetails?: CustomerDetails | null }).customerDetails ?? null);
            setResponse(null);
            setLeistungen(null);
            setLeistungenStale(true);
            lastProjectRef.current = null;
            setView('input');
            enqueueSnackbar(t('calculation.projectLoaded'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    // Öffnet ein Projekt über die URL (/kalkulation/projekt/:id) – der Effekt lädt es dann.
    const openProject = (id: string) => navigate(`/kalkulation/projekt/${id}`);

    // Aus der Übersicht: neue, eigene Konfiguration starten.
    const newConfiguration = () => {
        loadedIdRef.current = null;
        reset();
        navigate('/kalkulation');
        setMode('configure');
    };

    // Zurück zur Übersicht.
    const backToOverview = () => {
        loadedIdRef.current = null;
        navigate('/kalkulation');
        setMode('overview');
    };

    // Aus der Übersicht: JSON-Datei importieren → neue (ungespeicherte) Konfiguration.
    const importProjectFile = (file: File) => {
        loadedIdRef.current = null;
        reset();
        navigate('/kalkulation');
        setMode('configure');
        loadProjectFile(file);
    };

    // Projekt anhand der URL laden (Deep-Link / „Laden" aus der Übersicht), sobald Stammbaum bereit ist.
    useEffect(() => {
        if (!projectId || !tree || index.size === 0) return;
        if (loadedIdRef.current === projectId) return;
        loadedIdRef.current = projectId;
        setMode('configure');
        handleLoadProject(projectId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, tree, index]);

    const saveProject = () => {
        const project = collectProject(projektname, instances, index, standorte);
        const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        a.download = `${(project.projektname || 'projekt').replace(/[^\w.-]+/g, '_')}_${stamp}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const loadProjectFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const project = JSON.parse(String(reader.result));
                setProjektname(project.projektname || '');
                setInstances({ ...emptyInstances(index), ...projectToInstances(project) });
                setStandorte(withDefaultStandort(project.standorte));
                enqueueSnackbar(t('calculation.projectLoaded'), { variant: 'success' });
            } catch {
                enqueueSnackbar(t('calculation.invalidProject'), { variant: 'error' });
            }
        };
        reader.readAsText(file);
    };

    // Rich-Text-Kopie: bevorzugt Clipboard-API (HTML), sonst Auswahl eines gerenderten Knotens
    // + execCommand (funktioniert auch ohne sicheren Kontext, z. B. App über HTTP/IP).
    const copyRich = async (html: string): Promise<boolean> => {
        try {
            if (navigator.clipboard?.write && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([stripTags(html)], { type: 'text/plain' }),
                    }),
                ]);
                return true;
            }
        } catch {
            /* fällt auf die DOM-Auswahl zurück */
        }
        const node = clipRef.current;
        if (node) {
            node.innerHTML = html;
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(node);
            selection?.removeAllRanges();
            selection?.addRange(range);
            let ok = false;
            try {
                ok = document.execCommand('copy');
            } catch {
                ok = false;
            }
            selection?.removeAllRanges();
            node.innerHTML = '';
            if (ok) return true;
        }
        return execCommandCopyText(stripTags(html));
    };

    const copyFragment = async (kind: 'html' | 'markdown' | 'lvHtml' | 'lvMd') => {
        const fragments: OfferFragments | undefined = response?.fragments;
        let ok = false;
        if (kind === 'html' && fragments) {
            ok = await copyRich(fragments.html + (copyTable ? fragments.tableHtml : ''));
        } else if (kind === 'markdown' && fragments) {
            ok = await copyPlainText(fragments.markdown + (copyTable ? '\n' + fragments.tableMarkdown : ''));
        } else if (kind === 'lvHtml' && leistungen) {
            ok = await copyRich((lvMuss ? leistungen.lv : leistungen.leistungsangabe).html);
        } else if (kind === 'lvMd' && leistungen) {
            ok = await copyPlainText((lvMuss ? leistungen.lv : leistungen.leistungsangabe).markdown);
        }
        enqueueSnackbar(t(ok ? 'calculation.copied' : 'calculation.copyFailed'), {
            variant: ok ? 'success' : 'error',
        });
    };

    const exportToLabelWin = async () => {
        const project = collectProject(projektname, instances, index, standorte);
        if ((project.instanzen?.length ?? 0) === 0) {
            enqueueSnackbar(t('calculation.noInput'), { variant: 'warning' });
            return;
        }
        setBusy(true);
        try {
            const r = await calc.labelwinExport({ project });
            enqueueSnackbar(t('calculation.labelwin.exported', { count: r.written }), { variant: 'success' });
        } catch (e) {
            // 400 = Connector inaktiv ODER kein LabelBenutzer hinterlegt → Server-Meldung bevorzugen.
            const resp = (e as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
            if (resp?.status === 400) {
                enqueueSnackbar(resp.data?.error || resp.data?.message || t('calculation.labelwin.missingUser'), {
                    variant: 'warning',
                });
            } else {
                apiError(e);
            }
        } finally {
            setBusy(false);
        }
    };

    const pricesVisible = response?.pricesVisible !== false;
    const summary = pricesVisible ? response?.result.summary : undefined;
    const warnings = response?.result.warnings || [];
    const activeLv = leistungen ? (lvMuss ? leistungen.lv : leistungen.leistungsangabe) : null;

    if (mode === 'overview') {
        return (
            <Grid size={12}>
                <Title>{t('calculation.title')}</Title>
                <ProjectOverview onOpen={openProject} onNew={newConfiguration} onImport={importProjectFile} />
            </Grid>
        );
    }

    return (
        <Grid size={12}>
            <Title>{t('calculation.title')}</Title>

            <Paper sx={{ p: 1.5, mb: 2 }}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    {startsOnOverview && (
                        <Tooltip title={t('calculation.overview.back', 'Übersicht')}>
                            <IconButton color="primary" onClick={backToOverview}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <TextField
                        label={t('calculation.projectName')}
                        size="small"
                        value={projektname}
                        onChange={(e) => setProjektname(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                    {storedName && (
                        <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                            label={t('calculation.store.loadedAs', { name: storedName })}
                        />
                    )}

                    {/* Spacer schiebt die Aktionen nach rechts */}
                    <Box sx={{ flexGrow: 1 }} />

                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={canSeePrices ? faCalculator : faPaperPlane} />}
                        onClick={calculate}
                        disabled={busy || loading}
                    >
                        {canSeePrices ? t('calculation.calculate') : t('calculation.request', 'Anfragen')}
                    </Button>
                    <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faFloppyDisk} />} onClick={handleSave}>
                        {t('calculation.store.save')}
                    </Button>
                    {hasPermission('project.link.manage') && (
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                            onClick={() => setSendOpen(true)}
                        >
                            {t('calculation.store.send', 'Versenden')}
                        </Button>
                    )}
                    {canSeePrices && (
                        <Tooltip
                            title={!labelwinEnabled ? t('calculation.labelwin.inactive') : ''}
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<FontAwesomeIcon icon={faRightToBracket} />}
                                    onClick={exportToLabelWin}
                                    disabled={busy || !labelwinEnabled}
                                >
                                    {t('calculation.labelwin.export')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}

                    <Tooltip title={t('calculation.more', 'Weitere Aktionen')}>
                        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                        <MenuItem
                            onClick={() => {
                                setMenuAnchor(null);
                                setSaveOpen(true);
                            }}
                        >
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faCopy} />
                            </ListItemIcon>
                            {t('calculation.store.saveAs')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setMenuAnchor(null);
                                saveProject();
                            }}
                        >
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faDownload} />
                            </ListItemIcon>
                            {t('calculation.export')}
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                setMenuAnchor(null);
                                reset();
                            }}
                        >
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faRotateLeft} />
                            </ListItemIcon>
                            {t('calculation.reset')}
                        </MenuItem>
                    </Menu>
                </Stack>
            </Paper>

            <CustomerNoteBanner note={customerNotes} />

            {hasCustomerDetails(customerDetails) && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('calculation.mine.customerDetails', 'Kundendaten & Anschrift')}
                    </Typography>
                    <CustomerDetailsView details={customerDetails} />
                </Paper>
            )}

            <ToggleButtonGroup
                value={view}
                exclusive
                color="primary"
                size="small"
                onChange={(_, v) => v && setView(v)}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="input">
                    <FontAwesomeIcon icon={faListCheck} style={{ marginRight: 8 }} />
                    {t('calculation.input')}
                </ToggleButton>
                <ToggleButton value="result">
                    <FontAwesomeIcon icon={faCartShopping} style={{ marginRight: 8 }} />
                    {t('calculation.tabs.cart')}
                </ToggleButton>
            </ToggleButtonGroup>

            {(loading || busy) && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={2}>
                {/* Eingabe */}
                {view === 'input' && (
                <Grid size={12}>
                    <StandortManager standorte={standorte} onChange={setStandorte} />
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">{t('calculation.input')}</Typography>
                            <TextField
                                select
                                size="small"
                                label={t('calculation.order')}
                                value={reqOrder}
                                onChange={(e) => changeReqOrder(e.target.value as 'xml' | 'name')}
                            >
                                <MenuItem value="xml">{t('calculation.orderXml')}</MenuItem>
                                <MenuItem value="name">{t('calculation.orderName')}</MenuItem>
                            </TextField>
                        </Stack>
                        {tree ? (
                            <RequirementInputTree
                                tree={tree}
                                instances={instances}
                                standorte={standorte}
                                activeStandort={activeStandort}
                                onSelectStandort={setActiveStandort}
                                reqOrder={reqOrder}
                                addInstance={addInstance}
                                removeInstance={removeInstance}
                                updateInstance={updateInstance}
                                updateOpt={updateOpt}
                                ensureInstances={ensureInstances}
                            />
                        ) : (
                            !loading && <Typography color="text.secondary">{t('calculation.noData')}</Typography>
                        )}
                    </Paper>
                </Grid>
                )}

                {/* Ergebnis */}
                {view === 'result' && (
                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        {summary && (
                            <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, flexWrap: 'wrap' }}>
                                <Chip label={`${t('calculation.summary.base')}: ${money(summary.basisSystem)}`} />
                                {summary.softwareLoesung > 0 && (
                                    <Chip label={`${t('calculation.summary.software')}: ${money(summary.softwareLoesung)}`} />
                                )}
                                <Chip label={`${t('calculation.summary.units')}: ${money(summary.bedieneinheiten)}`} />
                                <Chip label={`${t('calculation.summary.service')}: ${money(summary.wartung)}`} />
                                <Chip color="primary" label={`${t('calculation.summary.total')}: ${money(summary.gesamt)}`} />
                            </Stack>
                        )}

                        {warnings.map((w, i) => (
                            <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                                {w}
                            </Alert>
                        ))}

                        <Tabs value={resultTab} onChange={onResultTabChange} variant="scrollable" sx={{ mb: 2 }}>
                            <Tab icon={<FontAwesomeIcon icon={faCartShopping} />} iconPosition="start" label={t('calculation.tabs.cart')} />
                            <Tab label={t('calculation.tabs.fragment')} />
                            <Tab label={t('calculation.tabs.features')} />
                            <Tab label={t('calculation.tabs.log')} />
                        </Tabs>

                        {!response && <Typography color="text.secondary">{t('calculation.runHint')}</Typography>}

                        {response && (resultTab === CART_TAB || resultTab === FRAGMENT_TAB) && (
                            <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FontAwesomeIcon icon={faCopy} />}
                                    onClick={() => copyFragment('html')}
                                >
                                    {t('calculation.copyFormatted')}
                                </Button>
                                <Button size="small" onClick={() => copyFragment('markdown')}>
                                    {t('calculation.copyMarkdown')}
                                </Button>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={copyTable} onChange={(e) => setCopyTable(e.target.checked)} />}
                                    label={t('calculation.copyTable')}
                                />
                            </Stack>
                        )}

                        {response && resultTab === CART_TAB && (
                            <Warenkorb
                                positions={response.result.positions}
                                summary={response.result.summary}
                                standorte={standorte}
                                pricesVisible={pricesVisible}
                            />
                        )}

                        {response && resultTab === FRAGMENT_TAB && (
                            <HtmlFragment html={(response.fragments.html || '') + (response.fragments.tableHtml || '')} />
                        )}

                        {response && resultTab === LV_TAB && (
                            <>
                                <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={lvMuss} onChange={(e) => setLvMuss(e.target.checked)} />}
                                        label={t('calculation.lvMuss')}
                                    />
                                    <Button size="small" startIcon={<FontAwesomeIcon icon={faCopy} />} onClick={() => copyFragment('lvHtml')}>
                                        {t('calculation.copyFormatted')}
                                    </Button>
                                    <Button size="small" onClick={() => copyFragment('lvMd')}>
                                        {t('calculation.copyMarkdown')}
                                    </Button>
                                </Stack>
                                {leistungen && leistungen.availableDomains.length > 0 && (
                                    <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
                                        {leistungen.availableDomains.map((d) => (
                                            <FormControlLabel
                                                key={d}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={lvSelected.has(d)}
                                                        onChange={(e) => toggleDomain(d, e.target.checked)}
                                                    />
                                                }
                                                label={d}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                <Divider sx={{ mb: 1 }} />
                                {leistungenLoading ? (
                                    <LinearProgress />
                                ) : activeLv && activeLv.html ? (
                                    <HtmlFragment html={activeLv.html} />
                                ) : (
                                    <Typography color="text.secondary">{t('calculation.noFeatures')}</Typography>
                                )}
                            </>
                        )}

                        {response && resultTab === LOG_TAB && (
                            <Box
                                component="pre"
                                sx={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'monospace', m: 0 }}
                            >
                                {(response.result.log || []).map((l) => `[${l.level}] ${l.message}`).join('\n')}
                            </Box>
                        )}
                    </Paper>
                </Grid>
                )}
            </Grid>

            <SaveProjectDialog
                open={saveOpen}
                title={t('calculation.store.saveAs')}
                initial={{
                    name: storedName || projektname || '',
                    customerReference,
                    customerName,
                    supportContractId,
                }}
                onClose={() => setSaveOpen(false)}
                onSubmit={handleSaveSubmit}
            />
            <SendInvitationDialog
                open={sendOpen}
                defaultName={storedName || projektname}
                defaultCustomerName={customerName}
                defaultCustomerReference={customerReference}
                onSend={handleSendInvitation}
                onClose={() => setSendOpen(false)}
            />

            {/* Verstecktes Ziel für den Rich-Text-Kopier-Fallback. */}
            <Box
                ref={clipRef}
                aria-hidden
                sx={{ position: 'fixed', left: -99999, top: 0, width: 1, height: 1, overflow: 'hidden' }}
            />
        </Grid>
    );
};

export default Calculation;
